import { Injectable, ElementRef, NgZone, QueryList } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { debounceTime, filter, map, startWith } from 'rxjs/operators';
import { EventLoggerService } from '@services/event-logger-service';
import { ElementTrackableDirective } from '@directives/element-trackable/element-trackable.directive';
import { Banner } from '@services/element-position-service/element-position.service.types';
import { AppConfig } from '../../app/app.config';

const MIXPANEL_REMOVED_EVENTS = [
  'diet_plan',
  'doctor_panel_and_testimonials',
  'referral',
  'quiz',
];

@Injectable({
  providedIn: 'root'
})
export class ElementPositionService {
  private banners: Banner[] = [];
  private readonly processedBanners = new Set<ElementRef>();
  private readonly observers = new Map<ElementRef, IntersectionObserver>();
  private readonly visibilitySubject = new Subject<{element: ElementRef, isVisible: boolean}>();
  public visibility$ = this.visibilitySubject.asObservable();

  private readonly registrationSubject = new Subject<() => Banner[]>();

  constructor(
    private readonly ngZone: NgZone,
    private readonly eventLogger: EventLoggerService,
    private readonly appConfig: AppConfig,
  ) {
    this.registrationSubject.pipe(
      debounceTime(2000),
    ).subscribe(resolveBanners => {
      const banners = resolveBanners();

      // Reset observers/banner list but keep processedBanners (only clearBanners() resets it) so
      // re-scans after async content loads (e.g. restock) don't replay already-sent impressions.
      this.observers.forEach(observer => observer.disconnect());
      this.observers.clear();
      this.banners = [];

      const sortedBanners = [...banners].sort((a, b) => this.getBannerKey(a) - this.getBannerKey(b));
      sortedBanners.forEach(banner => {
        this.banners.push(banner);
        this.observeElement(banner);
      });
    });
  }

  initializeTracking(trackableDirectives: QueryList<ElementTrackableDirective>): Observable<void> {
    return trackableDirectives.changes.pipe(
      startWith(trackableDirectives),
      filter(directives => !!directives),
      map((directives: QueryList<ElementTrackableDirective>) => {
        this.scan(directives);
      }),
    );
  }

  scan(trackableDirectives: QueryList<ElementTrackableDirective>): void {
    if (!trackableDirectives) return;
    this.registrationSubject.next(() => trackableDirectives.toArray()
      .filter((directive: ElementTrackableDirective) => directive.isValid())
      .map((directive: ElementTrackableDirective) => directive.toBanner()));
  }

  registerBanners(banners: Banner[]): void {
    this.registrationSubject.next(() => banners);
  }

  private observeElement(banner: Banner): void {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.processedBanners.has(banner.element)) {
            this.ngZone.run(() => {
              this.processedBanners.add(banner.element);
              this.logImpressionEvent(banner);
            });
          }
        });
      },
      {
        threshold: 1.0,
        rootMargin: '0px 0px 200px 0px'
      }
    );

    const elementToObserve = banner.carousel
      ? banner.element.nativeElement.closest('swiper, .swiper-container, [data-swiper]')
      : banner.element.nativeElement;

    if (elementToObserve) {
      observer.observe(elementToObserve);
      this.observers.set(banner.element, observer);
    }
  }

  private getBannerKey(banner: Banner): number {
    const nativeEl = banner.element.nativeElement;
    const elementToMeasure = banner.carousel
      ? nativeEl.closest('swiper, .swiper-container, [data-swiper]')
      : nativeEl;

    if (!elementToMeasure) return Number.MAX_SAFE_INTEGER;

    let currentEl = elementToMeasure;
    let top = 0;

    while (currentEl && currentEl !== document.body) {
      top += currentEl.offsetTop;
      currentEl = currentEl.offsetParent as HTMLElement;
    }

    const allElements = Array.from(document.body.getElementsByTagName('*'));
    const domIndex = allElements.indexOf(elementToMeasure);

    return top * 10000 + domIndex;
  }

  private getRequiredChannels(bannerName: string) {
    return MIXPANEL_REMOVED_EVENTS.includes(bannerName) ? [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ] : [];
  }

  private logImpressionEvent(banner: Banner): void {
    const channels = banner.channels ?? this.getRequiredChannels(banner.name);
    const positionIndex = this.getBannerOrder(banner.element);
    const eventData = {
      bannerName: banner.name,
      position: positionIndex + 1,
      pageName: banner.pageName,
      carousel: banner.carousel ?? false,
      ...banner.eventData
    };

    this.eventLogger.cleverTapEvent('impression', JSON.stringify(eventData), channels);
  }

  getBannerOrder(el: ElementRef): number {
    const sorted = this.banners.slice().sort((a, b) => this.getBannerKey(a) - this.getBannerKey(b));

    const groupIndices = new Map<number, number>();
    let groupCounter = 0;

    for (const banner of sorted) {
      const key = this.getBannerKey(banner);
      if (!groupIndices.has(key)) {
        groupIndices.set(key, groupCounter++);
      }
    }

    const banner = this.banners.find(b => b.element === el);
    if (banner) {
      const key = this.getBannerKey(banner);
      return groupIndices.get(key);
    }
    return -1;
  }

  clearBanners(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
    this.banners = [];
    this.processedBanners.clear();
  }
}
